import { useState } from 'react';
import { AlertCircle, CheckCircle2, ChevronDown } from 'lucide-react';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import { cn } from '@/lib/utils';
import {
  formatLockStatus,
  formatRemovalMethodology,
  resolveLockedByOperational,
  type LockCycleDisplayInput,
} from '@/lib/lockCycleDisplay';

type SelectedCycleLockDetailsProps = {
  lock: LockCycleDisplayInput & { lockedAt?: string | null };
};

function DetailRow({
  label,
  value,
  helperText,
}: {
  label: string;
  value: string;
  helperText?: string | null;
}) {
  return (
    <div>
      <p className="text-xs text-gray-600 font-medium">{label}</p>
      <p className="text-sm text-gray-900">{value}</p>
      {helperText ? (
        <p className="text-xs text-gray-500 mt-0.5">{helperText}</p>
      ) : null}
    </div>
  );
}

export function SelectedCycleLockDetails({ lock }: SelectedCycleLockDetailsProps) {
  const [technicalOpen, setTechnicalOpen] = useState(false);
  const lockedBy = resolveLockedByOperational(lock);
  const removal = formatRemovalMethodology(lock.removalGenerationMode);
  const warnings = lock.postLockWarnings ?? [];
  const status = formatLockStatus(warnings);

  return (
    <>
      <div className="pt-3 mt-3 border-t border-gray-200 space-y-3">
        <p className="text-xs font-semibold text-gray-700">Lock summary</p>

        <div>
          <p className="text-xs text-gray-600 font-medium">Locked by</p>
          <p className="text-sm text-gray-900">{lockedBy.primary}</p>
          {lockedBy.secondary ? (
            <p className="text-xs text-gray-500 mt-0.5">{lockedBy.secondary}</p>
          ) : null}
        </div>

        <DetailRow
          label="Locked on"
          value={
            lock.lockedAt
              ? new Date(lock.lockedAt).toLocaleString('en-US', {
                  month: 'short',
                  day: 'numeric',
                  year: 'numeric',
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : '—'
          }
        />

        <DetailRow
          label="Removal methodology"
          value={removal.label}
          helperText={removal.helperText}
        />

        <div>
          <p className="text-xs text-gray-600 font-medium">Lock status</p>
          {status.isHealthy ? (
            <p className="text-sm text-green-700 flex items-center gap-1.5">
              <CheckCircle2 className="h-4 w-4 shrink-0" aria-hidden="true" />
              {status.label}
            </p>
          ) : (
            <div className="space-y-0.5">
              <p className="text-sm text-amber-700 flex items-center gap-1.5">
                <AlertCircle className="h-4 w-4 shrink-0" aria-hidden="true" />
                {status.label}
              </p>
              {status.hint ? (
                <p className="text-xs text-amber-700/90 pl-5">{status.hint}</p>
              ) : null}
            </div>
          )}
        </div>

        {lock.lockReason === 'REASSIGNMENT' ? (
          <p className="text-xs text-amber-800 bg-amber-50 border border-amber-100 rounded px-2 py-1.5">
            Snapshot refreshed after an operator reassignment.
          </p>
        ) : null}
      </div>

      <Collapsible open={technicalOpen} onOpenChange={setTechnicalOpen} className="pt-3 mt-3 border-t border-gray-200">
        <CollapsibleTrigger className="flex w-full items-center justify-between text-left text-xs font-semibold text-gray-700 hover:text-gray-900">
          <span>Technical details</span>
          <ChevronDown
            className={cn('h-4 w-4 shrink-0 transition-transform', technicalOpen && 'rotate-180')}
            aria-hidden="true"
          />
        </CollapsibleTrigger>
        <CollapsibleContent className="pt-3 space-y-3">
          <DetailRow label="Lock row ID" value={String(lock.id)} />
          <DetailRow label="Lock version" value={lock.version != null ? String(lock.version) : '—'} />
          <DetailRow label="Lock reason" value={lock.lockReason || 'MANUAL'} />
          <DetailRow
            label="Locked by user ID"
            value={lock.lockedBy != null ? String(lock.lockedBy) : '—'}
          />
          <DetailRow
            label="Removal mode"
            value={lock.removalGenerationMode || '—'}
          />
          {lock.reassignmentId != null ? (
            <DetailRow label="Reassignment ID" value={String(lock.reassignmentId)} />
          ) : null}
          <div>
            <p className="text-xs text-gray-600 font-medium">Post-lock warnings</p>
            {warnings.length === 0 ? (
              <p className="text-sm text-gray-900">None</p>
            ) : (
              <ul className="mt-1 space-y-1.5">
                {warnings.map((warning, index) => (
                  <li key={`${warning.step}-${index}`} className="text-xs text-gray-700">
                    <span className="font-medium">{warning.step}</span>
                    {warning.message ? ` — ${warning.message}` : ''}
                  </li>
                ))}
              </ul>
            )}
          </div>
        </CollapsibleContent>
      </Collapsible>
    </>
  );
}
